import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import ReactGA from "react-ga4";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { useUserFeature } from "./useUserFeature";

// Track an exception event in GA4 (shows up in BigQuery as 'exception' event)
export function trackException(description: string, fatal: boolean = false) {
  try {
    if (!(window as any).__gaInitialized) return;
    ReactGA.gtag("event", "exception", {
      description: description.slice(0, 150), // GA4 max 150 chars
      fatal
    });
  } catch {
    // silently fail
  }
}

const APP_SOURCE = "customer_frontend_v2";

const useGATracking = (claritySessionId?: string | null) => {
  const location = useLocation();
  const { isWalletEnabled } = useUserFeature();
  const { userToken, account, userDetails, userPermission } = useSelector(
    (state: RootState) => state.authState
  );
  const initRef = useRef(false);
  const errorHandlersRef = useRef(false);

  // Initialize GA4 once
  useEffect(() => {
    if (!initRef.current) {
      try {
        ReactGA.initialize(process.env.REACT_APP_GOOGLE_ANALYTICS_ID || "UA-000000000");
        initRef.current = true;
        (window as any).__gaInitialized = true;
      } catch {
        // GA4 init failed
      }
    }
  }, []);

  // Global error handlers — catch unhandled JS errors and promise rejections
  useEffect(() => {
    if (!initRef.current || errorHandlersRef.current) return;
    errorHandlersRef.current = true;

    // Catch uncaught JS errors
    const handleError = (event: ErrorEvent) => {
      const desc = `${event.message} at ${event.filename}:${event.lineno}:${event.colno}`;
      trackException(desc, true);
    };

    // Catch unhandled promise rejections
    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const desc =
        reason instanceof Error ? `Unhandled: ${reason.message}` : `Unhandled: ${String(reason)}`;
      trackException(desc, false);
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  // Set user properties when auth state or clarity session changes
  useEffect(() => {
    if (!initRef.current) return;
    try {
      if (userToken && account?.id) {
        ReactGA.set({
          user_id: account.id.toString(),
          ...account
        });

        // Determine subscription status for BigQuery segmentation
        const isSubscribed = userDetails?.permissions?.is_subscribed ?? false;
        const isTrialing = userDetails?.permissions?.is_trialing ?? false;
        const factoringApproved = userDetails?.factoring_approved === "approved";
        const stripeEnabled = userDetails?.stripe_enabled ?? false;

        // Compute a single "account_status" for easy filtering
        let accountStatus = "free";
        if (isSubscribed) accountStatus = "paid";
        else if (isTrialing) accountStatus = "trial";
        else if (factoringApproved) accountStatus = "factoring_approved";

        const userTypes = userPermission?.user_type || [];
        const userTypeSorted = [...userTypes].sort().join(",");

        ReactGA.gtag("set", "user_properties", {
          app_source: APP_SOURCE,
          account_type: account.type || "",
          user_types: userTypeSorted || account.type || "",
          authenticated: "true",
          account_status: accountStatus,
          is_subscribed: isSubscribed ? "true" : "false",
          is_trialing: isTrialing ? "true" : "false",
          factoring_approved: factoringApproved ? "true" : "false",
          stripe_enabled: stripeEnabled ? "true" : "false",
          wallet_enabled: isWalletEnabled ? "true" : "false",
          has_factoring: userPermission?.has_factoring ? "true" : "false",
          dba: userPermission?.dba || "",
          ...(claritySessionId ? { clarity_session_id: claritySessionId } : {})
        });
      } else if (claritySessionId) {
        ReactGA.gtag("set", "user_properties", {
          app_source: APP_SOURCE,
          authenticated: "false",
          account_status: "anonymous",
          clarity_session_id: claritySessionId
        });
      }
    } catch {
      // GA4 set failed
    }
  }, [userToken, account, userDetails, userPermission, claritySessionId, isWalletEnabled]);

  // Track page views + business events on route change
  useEffect(() => {
    if (!initRef.current) return;
    try {
      ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });

      // Fire a navigation event for every path
      const pathSegments = location.pathname.split("/").filter(Boolean);
      const eventName =
        pathSegments.length > 0
          ? `nav_${pathSegments.join("_")}`.replace(/[^a-zA-Z0-9_]/g, "_").slice(0, 40)
          : "nav_home";
      ReactGA.event(eventName, { page: location.pathname });
    } catch {
      // GA4 tracking failed
    }
  }, [location]);
};

export default useGATracking;
